import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from '@/i18n';
import { clsx } from 'clsx';

interface EmailVerificationPageProps {
  token: string;
  /** Called when the user clicks "Go to login". Receives the verified email if known. */
  onSuccess: (verifiedEmail?: string) => void;
}

export const EmailVerificationPage: React.FC<EmailVerificationPageProps> = ({ token, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState<string | undefined>(undefined);

  const { verifyEmail } = useAuthStore();
  const { t } = useTranslation();

  useEffect(() => {
    let cancelled = false;

    const verify = async () => {
      try {
        await verifyEmail(token);
        if (!cancelled) {
          setSuccess(true);
          // Try to extract the email — the pending verification email
          // from the store might still be set.
          const pending = useAuthStore.getState().pendingVerificationEmail;
          if (pending) setVerifiedEmail(pending);
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message || t.auth.verificationFailed);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    verify();
    return () => { cancelled = true; };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 transition-colors">
        <div
          className={clsx(
            'w-full max-w-md rounded-xl border border-gray-200 dark:border-gray-700',
            'bg-white dark:bg-gray-800 shadow-lg p-8 text-center'
          )}
        >
          <Loader2 className="h-12 w-12 text-blue-600 dark:text-blue-400 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {t.auth.verifyingEmail}
          </h2>
          <p className="text-gray-500 dark:text-gray-400">{t.common.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 transition-colors">
      <div
        className={clsx(
          'w-full max-w-md rounded-xl border border-gray-200 dark:border-gray-700',
          'bg-white dark:bg-gray-800 shadow-lg p-8 text-center'
        )}
      >
        {success ? (
          <>
            <div className="flex justify-center mb-4">
              <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
              {t.auth.emailVerified}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t.auth.emailVerifiedHint}
            </p>
          </>
        ) : (
          <>
            <div className="flex justify-center mb-4">
              <XCircle className="h-12 w-12 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
              {t.auth.verificationFailed}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          </>
        )}

        <button
          onClick={() => onSuccess(verifiedEmail)}
          className={clsx(
            'w-full flex items-center justify-center px-4 py-2.5 rounded-lg font-medium text-sm text-white',
            'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
            'transition-colors'
          )}
        >
          {t.auth.goToLogin}
        </button>
      </div>
    </div>
  );
};
